import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, ImagePlus, FileText, Users2, MessageSquare, LogOut, Leaf, Menu, X, Circle, UserCog, Settings as SettingsIcon, Package, ShoppingBag, ShoppingCart, Download, Bell, BellRing, MonitorSmartphone, RefreshCw, LifeBuoy, Truck, Warehouse } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePWA } from "@/hooks/usePWA";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const nav = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/admin/realisations", label: "Réalisations", icon: ImagePlus },
  { to: "/admin/produits", label: "Produits", icon: Package },
  { to: "/admin/commandes", label: "Commandes", icon: ShoppingBag },
  { to: "/admin/flotte", label: "Flotte", icon: Truck },
  { to: "/admin/logistique", label: "Logistique", icon: Warehouse },
  { to: "/admin/paniers-abandonnes", label: "Paniers abandonnés", icon: ShoppingCart },
  { to: "/admin/clients", label: "Clients", icon: Users2 },
  { to: "/admin/seo", label: "Outil SEO", icon: FileText },
  { to: "/admin/leads", label: "Leads", icon: Users2 },
  { to: "/admin/sav", label: "Tickets SAV", icon: LifeBuoy },
  { to: "/admin/chat", label: "Chat en direct", icon: MessageSquare },
  { to: "/admin/connexions", label: "Connexions", icon: MonitorSmartphone, adminOnly: true },
  { to: "/admin/staff", label: "Équipe", icon: UserCog, adminOnly: true },
  { to: "/admin/settings", label: "Paramètres", icon: SettingsIcon, adminOnly: true },
];

export default function AdminLayout() {
  const { user, loading, logout, setUser } = useAuth();
  const nav_ = useNavigate();
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(false);
  const { deferredPrompt, installed, pushState, updateReady, promptInstall, applyUpdate, enablePush, isIOS, isStandalone } = usePWA();

  useEffect(() => { if (user) setOnline(!!user.is_online); }, [user]);

  // Update available banner
  useEffect(() => {
    if (updateReady) {
      toast.info("Nouvelle version disponible", {
        description: "Une mise à jour de l'app est prête.",
        duration: Infinity,
        action: { label: "Recharger", onClick: applyUpdate },
      });
    }
  }, [updateReady, applyUpdate]);

  // Session heartbeat (connexion tracking)
  useEffect(() => {
    if (!user) return;
    const beat = () => api.post("/sessions/heartbeat").catch(() => {});
    beat();
    const id = setInterval(beat, 60000);
    return () => clearInterval(id);
  }, [user]);

  const doInstall = async () => {
    if (isIOS && !isStandalone) {
      toast.info("Installer sur iPhone/iPad", { description: "Appuyez sur Partager puis « Sur l'écran d'accueil »." });
      return;
    }
    const ok = await promptInstall();
    if (ok) toast.success("Application installée");
  };

  const doEnablePush = async () => {
    const res = await enablePush();
    if (res.ok) toast.success("Notifications activées");
    else if (res.reason === "ios-not-installed") toast.info("Installez d'abord l'app", { description: "Ajoutez « Manager Acanthe » à l'écran d'accueil, puis ouvrez-la pour activer les notifications." });
    else if (res.reason === "denied") toast.error("Notifications refusées dans le navigateur");
    else toast.error("Notifications non supportées sur cet appareil");
  };

  useEffect(() => {
    if (!user) return;
    const audio = new Audio(fileUrl("acanthe-jardins/uploads/bdc4555a-2878-474e-9579-bc0a388be164.mp3"));
    let last = new Date().toISOString();
    const poll = async () => {
      try {
        const { data } = await api.get(`/notifications?since=${encodeURIComponent(last)}`);
        const fresh = (data.notifications || []).filter((n) => n.created_at > last);
        if (fresh.length) {
          last = fresh[0].created_at;
          audio.play().catch(() => {});
          fresh.forEach((n) => toast.success(`${n.title} · ${n.customer_name}`, { description: n.paid ? "Payée (CB)" : "À facturer (30j)" }));
        }
      } catch { /* ignore */ }
    };
    const id = setInterval(poll, 20000);
    return () => clearInterval(id);
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-forest text-cream">Chargement…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const toggleOnline = async () => {
    const next = !online;
    setOnline(next);
    await api.put(`/staff/${user.id}/status`, { is_online: next });
    setUser({ ...user, is_online: next });
  };

  const doLogout = async () => { await logout(); nav_("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#F7F7F8] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-forest text-cream flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6 flex items-center gap-3 border-b border-cream/10">
          <img src="/logo-acanthe-blanc.png" alt="Acanthe Jardins" className="h-10 w-auto" data-testid="admin-logo" />
          <span className="text-[11px] text-cream/50 border-l border-cream/20 pl-3">Back-office</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {nav.filter((n) => !n.adminOnly || user.role === "admin").map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-brand text-cream" : "text-cream/70 hover:bg-cream/10 hover:text-cream"}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-cream/10 space-y-3">
          {(!installed && (deferredPrompt || (isIOS && !isStandalone))) && (
            <button onClick={doInstall} data-testid="install-app"
              className="w-full flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm bg-brand text-cream hover:bg-brand/90 transition-colors font-medium">
              <Download size={16} /> Installer l'app
            </button>
          )}
          {pushState !== "granted" && pushState !== "unsupported" && (
            <button onClick={doEnablePush} data-testid="enable-push"
              className="w-full flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm bg-cream/10 hover:bg-cream/20 transition-colors">
              <Bell size={16} /> Activer les notifications
            </button>
          )}
          {pushState === "granted" && (
            <div className="w-full flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs text-green-300" data-testid="push-active">
              <BellRing size={15} /> Notifications activées
            </div>
          )}
          <button onClick={toggleOnline} data-testid="toggle-online"
            className="w-full flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm bg-cream/5 hover:bg-cream/10 transition-colors">
            <Circle size={10} className={online ? "fill-green-400 text-green-400" : "fill-stone text-stone"} />
            {online ? "En ligne (chat)" : "Hors ligne"}
          </button>
          <div className="flex items-center justify-between px-2">
            <div className="min-w-0"><p className="text-sm font-medium truncate">{user.name}</p><p className="text-[11px] text-cream/50 capitalize">{user.role}</p></div>
            <button onClick={doLogout} data-testid="admin-logout" className="p-2 rounded-lg hover:bg-cream/10"><LogOut size={18} /></button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-border px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu /></button>
          <span className="font-display text-lg">Acanthe Back-office</span>
          <span className="w-6" />
        </header>
        <main className="p-5 md:p-8 max-w-7xl mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
